import React, { useState, useEffect } from 'react';
import api from '../api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  FileText, 
  Target, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Package,
  Monitor,
  Wrench,
  Sparkles,
  Clock
} from 'lucide-react';

export const TestPlanTab = ({ projectId, onTestPlanUpdate }) => {
  const [testPlan, setTestPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(false);

  useEffect(() => {
    loadTestPlan();
  }, [projectId]);

  // Listen for test plan updates (after regeneration)
  useEffect(() => {
    if (onTestPlanUpdate) {
      loadTestPlan();
    }
  }, [onTestPlanUpdate]);

  const loadTestPlan = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/projects/${projectId}/test-plan`);
      setExists(response.data.exists);
      setTestPlan(response.data.test_plan);
    } catch (error) {
      console.error('Failed to load test plan:', error);
      toast.error('Failed to load test plan');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-azure" />
        <span className="ml-3 text-muted-foreground">Loading test plan...</span>
      </div>
    );
  }

  if (!exists || !testPlan) {
    return (
      <Card className="p-12 text-center border-dashed" data-testid="testplan-empty-state">
        <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-xl font-heading font-semibold mb-2 text-blueprint">No Test Plan Yet</h3>
        <p className="text-muted-foreground mb-2">
          Generate a QA Pack to automatically create a comprehensive test plan.
        </p>
        <p className="text-sm text-muted-foreground">
          Go to the Test Cases tab and click "Generate QA Pack"
        </p>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="testplan-content">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-semibold text-blueprint">Test Plan</h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {testPlan.generated_by_ai && (
            <Badge variant="secondary" className="gap-1">
              <Sparkles className="h-3 w-3" />
              AI Generated
            </Badge>
          )}
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {formatDate(testPlan.updated_at || testPlan.created_at)}
          </Badge>
        </div>
      </div>

      {/* Overview Section */}
      {testPlan.overview && (
        <Card className="p-6" data-testid="testplan-overview">
          <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-azure" />
            Overview
          </h3>
          <p className="text-foreground leading-relaxed">{testPlan.overview}</p>
        </Card>
      )}

      {/* Two Column Layout for Objectives and Test Approach */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Objectives */}
        {testPlan.objectives && testPlan.objectives.length > 0 && (
          <Card className="p-6" data-testid="testplan-objectives">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <Target className="h-5 w-5 text-azure" />
              Test Objectives
            </h3>
            <ul className="space-y-2">
              {testPlan.objectives.map((obj, idx) => (
                <li key={idx} className="flex items-start gap-2 text-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-azure flex-shrink-0" />
                  <span>{obj}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Test Approach */}
        {testPlan.test_approach && (
          <Card className="p-6" data-testid="testplan-approach">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <Target className="h-5 w-5 text-azure" />
              Test Approach
            </h3>
            <p className="text-foreground leading-relaxed">{testPlan.test_approach}</p>
          </Card>
        )}
      </div>

      {/* Scope Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* In Scope */}
        {testPlan.scope_in && testPlan.scope_in.length > 0 && (
          <Card className="p-6 border-l-4 border-l-green-500" data-testid="testplan-scope-in">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              In Scope
            </h3>
            <ul className="space-y-2">
              {testPlan.scope_in.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-foreground">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-500 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Out of Scope */}
        {testPlan.scope_out && testPlan.scope_out.length > 0 && (
          <Card className="p-6 border-l-4 border-l-red-400" data-testid="testplan-scope-out">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-400" />
              Out of Scope
            </h3>
            <ul className="space-y-2">
              {testPlan.scope_out.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-foreground">
                  <XCircle className="h-4 w-4 mt-0.5 text-red-400 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {/* Test Types */}
      {testPlan.test_types && testPlan.test_types.length > 0 && (
        <Card className="p-6" data-testid="testplan-test-types">
          <h3 className="text-lg font-heading font-semibold text-blueprint mb-3">Test Types</h3>
          <div className="flex flex-wrap gap-2">
            {testPlan.test_types.map((type, idx) => (
              <Badge key={idx} variant="secondary" className="px-3 py-1">
                {type}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Criteria Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entry Criteria */}
        {testPlan.entry_criteria && testPlan.entry_criteria.length > 0 && (
          <Card className="p-6" data-testid="testplan-entry-criteria">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-azure" />
              Entry Criteria
            </h3>
            <ul className="space-y-2">
              {testPlan.entry_criteria.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-foreground">
                  <span className="mt-2 h-1 w-1 rounded-full bg-azure flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Exit Criteria */}
        {testPlan.exit_criteria && testPlan.exit_criteria.length > 0 && (
          <Card className="p-6" data-testid="testplan-exit-criteria">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-azure" />
              Exit Criteria
            </h3>
            <ul className="space-y-2">
              {testPlan.exit_criteria.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-foreground">
                  <span className="mt-2 h-1 w-1 rounded-full bg-azure flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {/* Risks Section */}
      {testPlan.risks && testPlan.risks.length > 0 && (
        <Card className="p-6" data-testid="testplan-risks">
          <h3 className="text-lg font-heading font-semibold text-blueprint mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Risks & Mitigations
          </h3>
          <div className="space-y-4">
            {testPlan.risks.map((risk, idx) => (
              <div key={idx} className="border-l-2 border-amber-300 pl-4 py-2">
                <p className="font-medium text-foreground mb-1">
                  <span className="text-amber-600">Risk:</span> {risk.risk}
                </p>
                <p className="text-muted-foreground text-sm">
                  <span className="text-green-600 font-medium">Mitigation:</span> {risk.mitigation}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Bottom Row: Environment, Tools, Deliverables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Test Environment */}
        {testPlan.test_environment && (
          <Card className="p-6" data-testid="testplan-environment">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <Monitor className="h-5 w-5 text-azure" />
              Test Environment
            </h3>
            <p className="text-foreground text-sm leading-relaxed">{testPlan.test_environment}</p>
          </Card>
        )}

        {/* Tools */}
        {testPlan.tools && testPlan.tools.length > 0 && (
          <Card className="p-6" data-testid="testplan-tools">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <Wrench className="h-5 w-5 text-azure" />
              Tools
            </h3>
            <div className="flex flex-wrap gap-2">
              {testPlan.tools.map((tool, idx) => (
                <Badge key={idx} variant="outline" className="px-2 py-1 text-xs">
                  {tool}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Deliverables */}
        {testPlan.deliverables && testPlan.deliverables.length > 0 && (
          <Card className="p-6" data-testid="testplan-deliverables">
            <h3 className="text-lg font-heading font-semibold text-blueprint mb-3 flex items-center gap-2">
              <Package className="h-5 w-5 text-azure" />
              Deliverables
            </h3>
            <ul className="space-y-1">
              {testPlan.deliverables.map((item, idx) => (
                <li key={idx} className="text-sm text-foreground flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-azure" />
                  {item}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
};
